import Cookies from 'js-cookie';
import { gql } from '@apollo/client';
import * as Sentry from '@sentry/react';

import config from '../../aws-exports';
import { isSensitiveField } from './diff-paths';

// The caller's own staff-pool creation date. This used to be a browser-side AdminGetUser, which
// required giving every staff and agent session AdminGetUser on '*' (#36196).
const GET_MY_STAFF_USER_CREATED_DATE = gql`
  query GetMyStaffUserCreatedDate {
    getMyStaffUserCreatedDate
  }
`;

/**
 * @param {string} phoneNumber
 * @returns {string | null} E.164 formatted phone number (+11234567890) or null
 */
const tryConvertingPhoneToE164Number = (phoneNumber) => {
  // Remove all non-numeric characters
  let e164PhoneNumber = String(phoneNumber).replace(/\D/g, '');

  if (e164PhoneNumber.length > 10) {
    e164PhoneNumber = e164PhoneNumber.slice(-10);
  }

  if (e164PhoneNumber.length === 10) {
    return `+1${e164PhoneNumber}`;
  }

  return null;
};

export const identify = async (data, trackUrl = true) => {
  if (config.trackingEnabled && window.analytics) {
    if (data && data.phone) {
      data.phone = tryConvertingPhoneToE164Number(data.phone) || data.phone;
    }

    if (data && data.phoneNumber) {
      data.phoneNumber = tryConvertingPhoneToE164Number(data.phoneNumber) || data.phoneNumber;
    }

    // Get cognito user creation date
    let cognitoUserCreationDate = window.localStorage.getItem('cognitoUserCreationDate');
    if (!cognitoUserCreationDate) {
      try {
        // Dynamic import: core/apollo-client imports this module (for track), so a static import
        // would be circular.
        const { apolloClient } = await import('core/apollo-client');
        const { data: createdDateData } = await apolloClient.query({
          query: GET_MY_STAFF_USER_CREATED_DATE,
          fetchPolicy: 'no-cache'
        });
        cognitoUserCreationDate = createdDateData?.getMyStaffUserCreatedDate || null;
        if (cognitoUserCreationDate) {
          window.localStorage.setItem('cognitoUserCreationDate', cognitoUserCreationDate);
        }
      } catch (e) {
        // Non-fatal (identify() still proceeds without userCreatedDate), but report it: this was
        // one of three catches that made the v3 migration's failures invisible to Sentry (#33733).
        console.error('Error getting cognito user creation date: ', e);
        Sentry.captureException(e, { tags: { flow: 'analytics.identify' }, extra: { username: data.username } });
      }
    }

    data.userCreatedDate = cognitoUserCreationDate;
    const payload = trackUrl ? { ...data, mostRecentClusterUrl: window.location.href.substring(0, 1000) } : data;
    // Identify the user in Segment
    // remove all values where it's longer than 1000 bytes because it will not be accepted by segment
    for (const key of Object.keys(payload)) {
      if (String(payload[key]).length > 1000) {
        payload[key] = '';
      }
    }

    window.analytics.alias(`staff-${data.username}`);
    window.analytics.identify(`staff-${data.username}`, payload);

    // Identify the user in Logrocket
    import('logrocket').then(({ default: LogRocket }) => {
      if (data.username) {
        LogRocket.identify(`staff-${data.username}`, payload);
      }
    });
  }
};

const LOGROCKET_PII_KEYS = new Set([
  'email',
  'phone',
  'phoneNumber',
  'firstName',
  'lastName',
  'fname',
  'lname',
  'address'
]);

const toLogRocketProperties = (data) => {
  if (!data) {
    return undefined;
  }
  const isPrimitive = (value) => ['string', 'number', 'boolean'].includes(typeof value);
  const properties = Object.entries(data).reduce((acc, [key, value]) => {
    if (LOGROCKET_PII_KEYS.has(key) || isSensitiveField(key)) {
      return acc;
    }
    if (isPrimitive(value) || (Array.isArray(value) && value.every(isPrimitive))) {
      acc[key] = value;
    }
    return acc;
  }, {});
  return Object.keys(properties).length > 0 ? properties : undefined;
};

const isScalar = (value) => value == null || ['string', 'number', 'boolean'].includes(typeof value);

// Amplitude flattens a nested object into one event property per leaf key, and silently stops
// indexing new properties once a project passes its 2,000-property cap (#32980). Only scalars
// and arrays of scalars reach the wire; anything else is dropped and warned about in dev.
export const toFlatEventProperties = (event, data) => {
  if (!data) {
    return data;
  }
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (isScalar(value) || (Array.isArray(value) && value.every(isScalar))) {
      acc[key] = value;
    } else if (value instanceof Date) {
      acc[key] = value.toISOString();
    } else {
      const message = `track('${event}'): dropped object-valued property "${key}"; send flat scalars instead`;
      console.warn(message);
      if (!import.meta.env.DEV) {
        Sentry.captureMessage(message, 'warning');
      }
    }
    return acc;
  }, {});
};

export const track = (event, data, callback) => {
  if (config.trackingEnabled && window.analytics) {
    data = toFlatEventProperties(event, data);
    if (data && data.phone) {
      data.phone = tryConvertingPhoneToE164Number(data.phone) || data.phone;
    }

    if (data && data.phoneNumber) {
      data.phoneNumber = tryConvertingPhoneToE164Number(data.phoneNumber) || data.phoneNumber;
    }

    const branchUsername = window.sessionStorage.getItem('branchSessionUsername') || undefined;
    if (branchUsername) {
      data = { ...data, branchUsername };
    }

    const sessionId = Cookies.get('ampSessionId') || Date.now();
    window.analytics.track(event, data, { integrations: { Amplitude: { sessionId } } }, callback);
    const logRocketProperties = toLogRocketProperties(data);
    import('logrocket').then(({ default: LogRocket }) => LogRocket.track(event, logRocketProperties));
    Cookies.set('ampSessionId', sessionId, { expires: 0.5 / 24 }); // session expires after 30 min
  }
};

// Max wait for analytics.js to hand an event off before a hard navigation (window.location.*)
// would drop it from the queue.
const TRACK_BEFORE_NAVIGATION_TIMEOUT_MS = 1000;

export const trackAndWait = (event, data) =>
  new Promise((resolve) => {
    if (!config.trackingEnabled || !window.analytics) {
      resolve();
      return;
    }
    setTimeout(resolve, TRACK_BEFORE_NAVIGATION_TIMEOUT_MS);
    track(event, data, resolve);
  });
